import { NavLink } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { RxDashboard } from "react-icons/rx";
import { FaRegFileLines } from "react-icons/fa6";
import { FaRegFileWord } from "react-icons/fa";
import { GoGraph } from "react-icons/go";

const menuItems = [
  { name: "Dashboard", role: "TENANT_ADMIN", path: "/dashboard", icon: <RxDashboard /> },
  { name: "Report", role: "TENANT_ADMIN", path: "/report", icon: <FaRegFileLines /> },
  // { name: "Doc Editor", role: "TENANT_ADMIN", type: "link", path: "/document", icon: <FaRegFileWord /> },
  { name: "Graph", role: "SYS_ADMIN", type: "link", path: "/graph", icon: <GoGraph /> },
];

export default function Sidebar({ open = true, onClose = () => {} }) {
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUserData(parsed.user);
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
      }
    }
  }, []);

  // Unchanged from before: an item shows only for its own authority, so a
  // TENANT_ADMIN sees Dashboard/Report and a SYS_ADMIN sees Graph.
  const visibleItems = userData
    ? menuItems.filter((item) => item.role === userData.authority)
    : [];

  const initials = userData
    ? `${userData.first_name?.[0] || ""}${userData.last_name?.[0] || ""}`.toUpperCase() ||
      (userData.email?.[0] || "?").toUpperCase()
    : "";

  return (
    <aside
      className={
        "fixed left-0 top-0 z-40 flex h-full w-[280px] flex-col border-r border-bgray-200 " +
        "bg-white transition-transform duration-300 dark:border-darkblack-400 dark:bg-darkblack-600 " +
        (open ? "translate-x-0" : "-translate-x-full")
      }
    >
      {/* Brand */}
      <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-bgray-200 px-6 dark:border-darkblack-400">
        <div className="logo text-2xl">
          Tera<span className="span-color">Stamp</span>
        </div>
        {/* Closing is only meaningful while the drawer overlays the page. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="rounded-lg p-2 text-bgray-600 hover:bg-bgray-100 dark:text-bgray-50 dark:hover:bg-darkblack-500 xl:hidden"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M5 5L15 15M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-4 py-6">
        <h4 className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-bgray-500">
          Menu
        </h4>
        <ul className="space-y-1">
          {visibleItems.map((item) => (
            <li key={item.name}>
              <NavLink
                to={item.path}
                onClick={() => {
                  // On the overlay drawer, navigating should get out of the way.
                  if (window.innerWidth < 1280) onClose();
                }}
                className={({ isActive }) =>
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-medium transition-colors " +
                  (isActive
                    ? "bg-success-50 text-success-400"
                    : "text-bgray-700 hover:bg-bgray-100 dark:text-bgray-50 dark:hover:bg-darkblack-500")
                }
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.name}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Signed-in user */}
      {userData && (
        <div className="shrink-0 border-t border-bgray-200 px-4 py-4 dark:border-darkblack-400">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success-300 text-sm font-bold text-white">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-bgray-900 dark:text-white">
                {userData.first_name} {userData.last_name}
              </p>
              <p className="truncate text-xs text-bgray-500">{userData.email}</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
